import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ShieldCheck, Clock } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [tx, setTx] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        setTx(data.transaction);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        tries.current += 1;
        if (tries.current >= 20) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 3000);
      } catch (e) {
        if (e?.response?.status === 401) {
          window.location.href = `/login?next=${encodeURIComponent(`/payment/success?session_id=${sessionId}`)}`;
          return;
        }
        tries.current += 1;
        if (tries.current >= 20) {
          setStatus("error");
        } else {
          setTimeout(poll, 3000);
        }
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-20">
        <div className="border-2 border-[#0A0A0B] bg-white p-10 text-center">
          {status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#FF4D00]" />
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 mt-6">
                // Vérification
              </div>
              <h1 className="display text-4xl mt-2">Validation du paiement…</h1>
              <p className="text-zinc-600 mt-3 text-sm">Patientez quelques secondes.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="w-16 h-16 bg-emerald-600 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-white" strokeWidth={2.5} />
              </div>
              <div className="mono text-[11px] uppercase tracking-widest text-emerald-700 mt-6">
                // Succès
              </div>
              <h1 className="display text-4xl mt-2">Paiement séquestré ✓</h1>
              <p className="text-zinc-600 mt-3">
                Les fonds sont bloqués chez Batisur. L'artisan peut démarrer le chantier.
              </p>
              {tx && (
                <div className="mt-6 border-2 border-[#0A0A0B] grid grid-cols-2">
                  <div className="p-3 border-r-2 border-[#0A0A0B]">
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Montant</div>
                    <div className="display text-xl mt-1">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(tx.amount)}</div>
                  </div>
                  <div className="p-3">
                    <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Statut</div>
                    <div className="display text-xl mt-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FF4D00]" /> Séquestré</div>
                  </div>
                </div>
              )}
              <Link to={tx ? `/missions/${tx.mission_id}` : "/dashboard"}>
                <button className="btn-batisur mt-8" data-testid="back-to-mission">Voir la mission</button>
              </Link>
            </>
          )}
          {(status === "expired" || status === "error") && (
            <>
              {status === "expired" ? (
                <XCircle className="w-12 h-12 mx-auto text-red-600" />
              ) : (
                <Clock className="w-12 h-12 mx-auto text-amber-600" />
              )}
              <h1 className="display text-4xl mt-4">
                {status === "expired" ? "Session de paiement expirée" : "Vérification en cours"}
              </h1>
              <p className="text-zinc-600 mt-3">
                {status === "expired"
                  ? "La session Stripe a expiré sans paiement. Réessayez depuis votre mission."
                  : "Si vous avez été débité, pas d'inquiétude : le rapprochement est automatique et vos fonds seront bien séquestrés sous quelques minutes. Vérifiez votre mission ou contactez-nous si le statut n'évolue pas."}
              </p>
              <Link to={tx ? `/missions/${tx.mission_id}` : "/dashboard"}>
                <button className="btn-anthracite mt-6" data-testid="back-to-dashboard">
                  {tx ? "Voir la mission" : "Retour au dashboard"}
                </button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
